import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Loader2, Send } from "lucide-react";
import Seo from "../components/Seo";
import CallbackForm from "../components/CallbackForm";
import { api, track } from "../lib/api";
import { toast } from "sonner";

export default function Contact() {
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "", company: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || (!f.email.trim() && !f.phone.trim())) { toast.error("Add your name and a way to reach you."); return; }
    setLoading(true);
    try { await api.post("/submissions", { kind: "contact", ...f }); setDone(true); }
    catch { toast.error("Something went wrong. Please call 0431 206 206."); }
    finally { setLoading(false); }
  };

  const input = "w-full rounded-xl border-2 border-brand-line px-4 py-3 outline-none focus:border-brand-gold";

  return (
    <>
      <Seo title="Contact Car To Cash | Call 0431 206 206" description="Get in touch with Car To Cash for a free car valuation. Call 0431 206 206, email info@cartocash.com.au, or send us a message." canonical="https://cartocash.com.au/contact" />
      <section className="noise-dark text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
          <h1 className="font-display text-4xl sm:text-5xl font-black">Get in touch</h1>
          <p className="mt-3 text-white/75 text-lg">Call us, email us, or send a message — we're here 7 days a week.</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16 grid lg:grid-cols-2 gap-10">
        <div>
          <div className="grid sm:grid-cols-2 gap-4">
            <a href="tel:0431206206" onClick={() => track("phone_tap", { where: "contact" })} className="rounded-2xl border border-brand-line bg-white p-6 hover:-translate-y-1 hover:shadow-lg transition-all" data-testid="contact-phone">
              <Phone className="h-7 w-7 text-brand-gold" /><p className="mt-3 text-xs font-bold uppercase text-brand-muted">Call us</p><p className="font-display font-extrabold text-brand-navy text-lg">0431 206 206</p>
            </a>
            <a href="mailto:info@cartocash.com.au" onClick={() => track("email_tap")} className="rounded-2xl border border-brand-line bg-white p-6 hover:-translate-y-1 hover:shadow-lg transition-all" data-testid="contact-email">
              <Mail className="h-7 w-7 text-brand-gold" /><p className="mt-3 text-xs font-bold uppercase text-brand-muted">Email</p><p className="font-display font-extrabold text-brand-navy text-sm break-all">info@cartocash.com.au</p>
            </a>
            <div className="rounded-2xl border border-brand-line bg-white p-6"><MapPin className="h-7 w-7 text-brand-gold" /><p className="mt-3 text-xs font-bold uppercase text-brand-muted">Service area</p><p className="font-display font-extrabold text-brand-navy">Melbourne &amp; regional Victoria</p></div>
            <div className="rounded-2xl border border-brand-line bg-white p-6"><Clock className="h-7 w-7 text-brand-gold" /><p className="mt-3 text-xs font-bold uppercase text-brand-muted">Hours</p><p className="font-display font-extrabold text-brand-navy">Mon–Sun, 7am–7pm</p></div>
          </div>
          <div className="mt-6"><CallbackForm /></div>
        </div>

        <div>
          {done ? (
            <div className="rounded-2xl border border-brand-line bg-brand-surface p-10 text-center" data-testid="contact-success">
              <Send className="mx-auto h-10 w-10 text-brand-gold" />
              <h2 className="mt-4 font-display text-2xl font-extrabold text-brand-navy">Message sent!</h2>
              <p className="mt-2 text-brand-muted">Thanks for reaching out. We'll get back to you shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-2xl border border-brand-line bg-white p-6 sm:p-8 shadow-lg" data-testid="contact-form">
              <h2 className="font-display text-xl font-extrabold text-brand-navy">Send us a message</h2>
              <input type="text" tabIndex={-1} autoComplete="off" value={f.company} onChange={(e) => set("company", e.target.value)} className="hidden" aria-hidden="true" />
              <div className="mt-5 space-y-4">
                <input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" className={input} data-testid="contact-name" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <input value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="Email" inputMode="email" className={input} data-testid="contact-email-input" />
                  <input value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone" inputMode="tel" className={input} data-testid="contact-phone-input" />
                </div>
                <textarea value={f.message} onChange={(e) => set("message", e.target.value)} placeholder="How can we help?" rows={5} className={input} data-testid="contact-message" />
                <button disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-navy px-6 py-3.5 font-bold text-white hover:bg-brand-navy2 transition-colors disabled:opacity-60" data-testid="contact-submit">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send message
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </>
  );
}
